import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, fileUrl } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { useContentTranslation } from "../lib/useContentTranslation";
import Navbar from "../components/Navbar";
import SEOHead from "../components/SEOHead";
import Footer from "../components/Footer";
import ConciergeWidget from "../components/ConciergeWidget";
import { CryptoDonateButton } from "../components/CryptoDonateButton";
import { motion } from "framer-motion";
import { HandHeart, ArrowRight, ChatCircleDots, Clock } from "@phosphor-icons/react";
import MilestoneCelebrator from "../components/MilestoneCelebrator";
import CampaignUpdates from "../components/CampaignUpdates";
import CampaignShareRow from "../components/CampaignShareRow";
import LivestreamEmbed from "../components/LivestreamEmbed";
import EmployerSearch from "../components/EmployerSearch";
import PayPalButtons from "../components/PayPalButtons";
import StickyGiveBar from "../components/StickyGiveBar";
import DonorInstallStrip from "../components/DonorInstallStrip";

export default function GivingPage() {
    const { slug, pageSlug } = useParams();
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    // Personalized ask: a gift officer can share a link carrying suggested
    // amounts + the donor's name (e.g. from a capacity-rated major-gift appeal).
    const askAmounts = useMemo(() => {
        const raw = searchParams.get("amts");
        if (!raw) return null;
        const arr = raw.split(",").map(s => parseInt(s, 10)).filter(n => n > 0);
        return arr.length ? arr : null;
    }, [searchParams]);
    const { t, i18n } = useTranslation();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    // AI-translated org content (title/story) for the active locale
    const ptr = useContentTranslation({
        title: data?.page?.title || "",
        story: data?.page?.story || "",
    });

    const [amount, setAmount] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [anon, setAnon] = useState(false);
    const [busy, setBusy] = useState(false);
    const [submitErr, setSubmitErr] = useState("");
    const [frequency, setFrequency] = useState("one-time");
    // Tribute giving
    const [tributeOpen, setTributeOpen] = useState(false);
    const [tributeType, setTributeType] = useState("honor");
    const [tributeName, setTributeName] = useState("");
    const [tributeNotifyEmail, setTributeNotifyEmail] = useState("");
    const [tributeNotifyName, setTributeNotifyName] = useState("");
    const [tributeNotifyMessage, setTributeNotifyMessage] = useState("");
    const [employer, setEmployer] = useState(null);
    // Round-up: add change up to next $5 increment
    const [roundUp, setRoundUp] = useState(false);
    // Cover the fees: donor absorbs platform + card fees so ~100% reaches the cause
    const [coverFees, setCoverFees] = useState(true);

    useEffect(() => {
        api.get(`/giving-pages/public/${slug}/${pageSlug}`)
            .then(r => {
                setData(r.data);
                const firstAmt = (askAmounts && askAmounts[0]) || r.data.page.suggested_amounts?.[0] || 50;
                setAmount(parseInt(searchParams.get("amt"), 10) || firstAmt);
                if (searchParams.get("name")) setName(searchParams.get("name"));
                if (searchParams.get("email")) setEmail(searchParams.get("email"));
                // Default the toggle to the EC's page setting (defaults ON).
                setCoverFees(r.data.page.cover_fees_default !== false);
            })
            .catch(e => setErr(e.response?.data?.detail || "Not found"));
    }, [slug, pageSlug]);

    const [verifyGate, setVerifyGate] = useState(null);  // { email, sent, message } when blocked
    const [resendingVerify, setResendingVerify] = useState(false);

    const donate = async (e) => {
        e.preventDefault();
        setBusy(true); setSubmitErr(""); setVerifyGate(null);
        try {
            const numericAmt = parseFloat(amount) || 0;
            const effectiveAmt = roundUp ? Math.ceil(numericAmt / 5) * 5 : numericAmt;
            const tributePayload = tributeOpen && tributeName.trim()
                ? {
                    tribute_type: tributeType,
                    tribute_name: tributeName.trim(),
                    tribute_notify_email: tributeNotifyEmail.trim(),
                    tribute_notify_name: tributeNotifyName.trim(),
                    tribute_notify_message: tributeNotifyMessage.trim(),
                  }
                : {};
            if (frequency === "one-time") {
                const { data: res } = await api.post("/donations/checkout", {
                    org_slug: slug,
                    giving_page_slug: pageSlug,
                    amount: effectiveAmt,                    donor_name: name, donor_email: email, message,
                    is_anonymous: anon,
                    cover_fees: coverFees,
                    origin_url: window.location.origin,
                    lang: (i18n.language || "en").split("-")[0],
                    employer_id: employer?.id || null,
                    employer_name: employer?.name || "",
                    ...tributePayload,
                });
                if (res?.requires_email_verification) {
                    setVerifyGate({ email: res.email, sent: res.sent, message: res.message });
                    setBusy(false);
                    return;
                }
                trackEvent("begin_checkout", {
                    currency: "USD",
                    value: effectiveAmt,
                    items: [{ item_id: "donation_one_time", item_name: "One-time donation", item_category: "donation", price: effectiveAmt, quantity: 1 }],
                    org_slug: slug,
                    giving_page_slug: pageSlug,
                });
                window.location.href = res.url;
            } else {
                const { data: res } = await api.post("/recurring/checkout", {
                    org_slug: slug,
                    giving_page_slug: pageSlug,
                    interval: frequency,
                    amount: effectiveAmt,
                    donor_name: name, donor_email: email, message,
                    is_anonymous: anon,
                    cover_fees: coverFees,
                    origin_url: window.location.origin,
                    lang: (i18n.language || "en").split("-")[0],
                });
                if (res?.requires_email_verification) {
                    setVerifyGate({ email: res.email, sent: res.sent, message: res.message });
                    setBusy(false);
                    return;
                }
                window.location.href = res.url;
            }
        } catch (e) {
            setSubmitErr(e.response?.data?.detail || t("giving.donationFailed"));
        } finally { setBusy(false); }
    };

    const resendVerifyAndRetry = async () => {
        setResendingVerify(true);
        try {
            await api.post("/email/verification/resend", { email, purpose: "donation", name });
            setVerifyGate(g => ({ ...g, sent: true }));
        } catch (e) {
            setSubmitErr(e.response?.data?.detail || t("giving.couldNotResend"));
        } finally { setResendingVerify(false); }
    };

    if (err) return (
        <div className="min-h-screen bg-background"><Navbar />
            <div className="pt-40 text-center"><h1 className="font-heading text-5xl">{t("giving.pageNotFound")}</h1></div>
        </div>
    );
    if (!data) return <div className="min-h-screen flex items-center justify-center"><p className="label-eyebrow">{t("common.loading")}</p></div>;

    const { org, page, recent_donations, match_challenge } = data;
    const pct = Math.min(100, (page.total_raised / Math.max(1, page.goal)) * 100);
    const numericAmt = parseFloat(amount) || 0;
    const roundUpTarget = Math.ceil(numericAmt / 5) * 5;
    const roundUpDelta = Math.max(0, roundUpTarget - numericAmt);
    const roundedAmt = roundUp ? roundUpTarget : numericAmt;
    // Cover-the-fees preview (server recomputes authoritatively). Platform % from
    // the org if exposed, else the 2% default; card fee = 2.9% + $0.30.
    const platformPct = (data?.org?.platform_fee_pct ?? 2) / 100;
    const feesAdded = coverFees && roundedAmt > 0
        ? roundedAmt * platformPct + (roundedAmt * 0.029 + 0.30)
        : 0;
    const total = roundedAmt + feesAdded;

    return (
        <div className="min-h-screen bg-background" data-testid="giving-page" data-no-translate>
            <SEOHead
                title={`${ptr.title || page.title} — ${org.name}`}
                description={(ptr.story || page.story || `Support ${org.name}. Every gift makes a difference.`).slice(0, 160)}
                canonical={`/give/${slug}/${pageSlug}`}
            />
            <Navbar />
            {match_challenge && (
                <div className="bg-[#A93226] text-white px-4 py-3 text-center sticky top-16 z-30 shadow-md" data-testid="match-challenge-banner">
                    <p className="font-heading uppercase tracking-wider text-base md:text-lg leading-tight font-bold">
                        {match_challenge.headline}
                    </p>
                    {match_challenge.sponsor && (
                        <p className="text-xs md:text-sm italic opacity-90 mt-0.5">{match_challenge.sponsor}</p>
                    )}
                    {(match_challenge.matched_so_far || 0) > 0 && (
                        <p className="text-xs md:text-sm mt-1 opacity-95" data-testid="match-challenge-matched-so-far">
                            <span className="font-bold">${(match_challenge.matched_so_far).toLocaleString(undefined, { minimumFractionDigits: 0 })}</span> matched so far tonight
                        </p>
                    )}
                </div>
            )}
            <MilestoneCelebrator pct={pct} goalKey={`give-${page.id}`} />

            <section className="relative pt-32 pb-12 px-6 md:px-12 lg:px-24 bg-primary text-primary-foreground overflow-hidden">
                {page.image_url && (
                    <div className="absolute inset-0 opacity-25">
                        <img src={fileUrl(page.image_url)} alt="" className="w-full h-full object-cover" />
                    </div>
                )}
                <div className="relative z-10 max-w-5xl">
                    <p className="label-eyebrow text-accent">{org.name} · {t("giving.makeGift")}</p>
                    <h1 className="font-heading text-5xl md:text-7xl mt-3 leading-tight" data-testid="giving-page-title">{ptr.title || page.title}</h1>
                </div>
            </section>

            <div className="px-6 md:px-12 lg:px-24 py-16 max-w-6xl mx-auto grid md:grid-cols-12 gap-12">
                <div className="md:col-span-7 space-y-10">
                    {page.livestream_url && <LivestreamEmbed url={page.livestream_url} />}

                    <div>
                        <p className="text-lg leading-relaxed text-foreground" data-testid="giving-page-story">
                            {ptr.story || page.story || `Your gift powers ${org.name}'s mission. Every dollar matters.`}
                        </p>
                    </div>

                    <div className="ed-card p-8" data-testid="thermometer">
                        <div className="flex items-end justify-between mb-3">
                            <div>
                                <p className="label-eyebrow">{t("giving.raisedSoFar")}</p>
                                <p className="data-num text-4xl mt-1">${page.total_raised.toLocaleString()}</p>
                            </div>
                            <div className="text-right">
                                <p className="label-eyebrow">{t("giving.goal")}</p>
                                <p className="data-num text-lg text-muted-foreground">${page.goal.toLocaleString()}</p>
                            </div>
                        </div>
                        <div className="h-4 bg-muted relative overflow-hidden">
                            <motion.div
                                initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1.4, ease: "easeOut" }}
                                className="absolute inset-y-0 left-0 bg-accent"
                            />
                        </div>
                        <p className="text-xs label-eyebrow mt-2 text-accent" data-testid="thermometer-pct">{pct.toFixed(1)}% {t("giving.ofGoal")} · {page.donor_count} {t("giving.donors")}</p>
                        {(() => {
                            if (!page.deadline) return null;
                            const end = new Date(page.deadline);
                            if (isNaN(end.getTime())) return null;
                            const days = Math.ceil((end.getTime() - Date.now()) / 86400000);
                            return (
                                <p className="text-xs mt-2 flex items-center gap-1.5 font-semibold" style={{ color: "var(--accent)" }} data-testid="giving-countdown">
                                    <Clock size={13} weight="bold" />
                                    {days > 1 ? `${days} days left to give` : days === 1 ? "1 day left to give" : days === 0 ? "Final day — give today!" : "Campaign ended"}
                                </p>
                            );
                        })()}
                    </div>

                    {page.social_share_enabled !== false && (
                        <div className="ed-card p-6" data-testid="giving-share-card">
                            <CampaignShareRow
                                url={typeof window !== "undefined" ? window.location.href : ""}
                                title={page.title}
                                message={page.share_message}
                            />
                            <p className="text-xs text-muted-foreground mt-2">No donation needed to help — sharing reaches friends who can.</p>
                        </div>
                    )}

                    <CampaignUpdates parentType="giving_page" parentId={page.id} />

                    {recent_donations.length > 0 && (
                        <div>
                            <p className="label-eyebrow text-accent">Recent donors</p>
                            <div className="ed-card divide-y divide-border mt-3" data-testid="recent-donors">
                                {recent_donations.map(d => (
                                    <div key={d.id} className="px-5 py-3 flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <p className="font-body">{d.is_anonymous ? "Anonymous" : d.donor_name}</p>
                                            {d.message && (
                                                <p className="text-sm text-muted-foreground italic mt-1 flex items-start gap-1">
                                                    <ChatCircleDots size={12} weight="thin" className="mt-1 shrink-0" />
                                                    <span>"{d.message}"</span>
                                                </p>
                                            )}
                                        </div>
                                        <p className="data-num">${d.amount.toFixed(0)}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <form onSubmit={donate} id="donate-form" className="md:col-span-5 ed-card p-8 space-y-6 h-fit sticky top-24" data-testid="donate-form">
                    <div>
                        <HandHeart size={36} weight="thin" className="text-accent mb-3" />
                        <h2 className="font-heading text-3xl">{t("giving.makeGift")}</h2>
                        <p className="text-xs label-eyebrow text-accent mt-2">{t("giving.fundsGoTo", { org: org.name })}</p>
                    </div>

                    <div data-testid="frequency-selector">
                        <label className="label-eyebrow block mb-2">{t("giving.frequency")}</label>
                        <div className="grid grid-cols-4 gap-1.5">
                            {[
                                { v: "one-time", l: t("giving.oneTime") },
                                { v: "monthly", l: t("giving.monthly") },
                                { v: "quarterly", l: t("giving.quarterly") },
                                { v: "yearly", l: t("giving.yearly") },
                            ].map(o => (
                                <button type="button" key={o.v} onClick={() => setFrequency(o.v)}
                                    className={`py-2 px-1 border text-[10px] font-bold uppercase tracking-wide text-center leading-tight ${frequency === o.v ? "border-accent bg-accent text-accent-foreground" : "border-border hover:border-accent"}`}
                                    data-testid={`frequency-${o.v}`}>
                                    {o.l}
                                </button>
                            ))}
                        </div>
                        {frequency !== "one-time" && (
                            <p className="text-xs text-muted-foreground mt-2">Charged automatically every {frequency === "monthly" ? "month" : frequency === "quarterly" ? "3 months" : "year"}.</p>
                        )}
                    </div>

                    <div>
                        <label className="label-eyebrow block mb-3">{t("giving.amount")}</label>
                        {(page.donation_tiers || []).length > 0 && (
                            <div className="space-y-2 mb-3" data-testid="impact-tiers">
                                {page.donation_tiers.map((t, i) => (
                                    <button type="button" key={i} onClick={() => setAmount(t.amount)}
                                        className={`w-full text-left p-3 border ${parseFloat(amount) === t.amount ? "border-accent bg-accent/5" : "border-border hover:border-accent"} transition-colors`}
                                        data-testid={`impact-tier-${t.amount}`}>
                                        <div className="flex items-baseline justify-between gap-3">
                                            <span className="data-num text-lg">${t.amount}</span>
                                            <span className="text-xs text-muted-foreground flex-1">{t.description}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="grid grid-cols-3 gap-2 mb-3">
                            {(askAmounts || page.suggested_amounts || []).map(a => (
                                <button type="button" key={a} onClick={() => setAmount(a)}
                                        className={`py-3 border ${parseFloat(amount) === a ? "border-accent bg-accent text-accent-foreground" : "border-border hover:border-accent"} data-num`}
                                        data-testid={`amount-preset-${a}`}>
                                    ${a}
                                </button>
                            ))}
                        </div>
                        <input className="ed-input data-num text-2xl" type="number" step="1" min="1"
                               value={amount} onChange={e => setAmount(e.target.value)}
                               placeholder="$"
                               data-testid="donate-amount-input" />
                    </div>

                    <div>
                        <label className="label-eyebrow block mb-1">{t("giving.yourName")}</label>
                        <input className="ed-input" required value={name} onChange={e => setName(e.target.value)} data-testid="donate-name" />
                    </div>
                    <div>
                        <label className="label-eyebrow block mb-1">{t("giving.email")}</label>
                        <input className="ed-input" type="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="donate-email" />
                    </div>
                    <div>
                        <label className="label-eyebrow block mb-1">{t("giving.message")}</label>
                        <textarea className="ed-textarea" rows={2} value={message} onChange={e => setMessage(e.target.value)} placeholder="" data-testid="donate-message" />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={anon} onChange={e => setAnon(e.target.checked)} data-testid="donate-anon" />
                        <span>{t("giving.anonymous")}</span>
                    </label>

                    {/* Employer matching gift */}
                    <EmployerSearch value={employer} onChange={setEmployer} />

                    {/* Round-up */}
                    {numericAmt > 0 && Math.ceil(numericAmt / 5) * 5 !== numericAmt && (
                        <label className="flex items-start gap-2 text-sm border border-border p-3 bg-muted/30 cursor-pointer" data-testid="donate-roundup">
                            <input type="checkbox" checked={roundUp} onChange={e => setRoundUp(e.target.checked)} className="mt-1" />
                            <span>
                                <span className="font-medium">{t("giving.roundUp", { amount: (Math.ceil(numericAmt / 5) * 5).toFixed(0) })}</span>{" "}
                                <span className="text-muted-foreground">— +${roundUpDelta.toFixed(2)}</span>
                            </span>
                        </label>
                    )}

                    {/* Tribute giving */}
                    <div className="border border-border p-3" data-testid="tribute-section">
                        <button type="button" onClick={() => setTributeOpen(!tributeOpen)} className="w-full flex items-center justify-between text-sm" data-testid="tribute-toggle">
                            <span className="flex items-center gap-2">
                                <HandHeart size={16} className="text-accent" weight="fill" />
                                <span className="font-medium">Give in honor or memory of someone</span>
                            </span>
                            <span className="text-xs text-muted-foreground">{tributeOpen ? "Close" : "Add tribute"}</span>
                        </button>
                        {tributeOpen && (
                            <div className="mt-3 space-y-3 pt-3 border-t border-border">
                                <div className="flex gap-2">
                                    {[["honor", "In honor of"], ["memory", "In memory of"]].map(([v, l]) => (
                                        <button type="button" key={v} onClick={() => setTributeType(v)}
                                            className={`flex-1 py-2 text-xs uppercase tracking-wider border ${tributeType === v ? "border-accent bg-accent text-accent-foreground" : "border-border hover:border-accent"}`}
                                            data-testid={`tribute-type-${v}`}>
                                            {l}
                                        </button>
                                    ))}
                                </div>
                                <input className="ed-input" placeholder="Honoree's full name" value={tributeName} onChange={e => setTributeName(e.target.value)} data-testid="tribute-name" />
                                <p className="text-xs text-muted-foreground">Optionally, notify someone of your gift (amount kept private):</p>
                                <input className="ed-input" placeholder="Family member's name (optional)" value={tributeNotifyName} onChange={e => setTributeNotifyName(e.target.value)} data-testid="tribute-notify-name" />
                                <input className="ed-input" type="email" placeholder="Family member's email (optional)" value={tributeNotifyEmail} onChange={e => setTributeNotifyEmail(e.target.value)} data-testid="tribute-notify-email" />
                                <textarea className="ed-textarea" rows={2} placeholder="Personal message (optional)" value={tributeNotifyMessage} onChange={e => setTributeNotifyMessage(e.target.value)} data-testid="tribute-notify-message" />
                            </div>
                        )}
                    </div>

                    {/* Cover the fees */}
                    {numericAmt > 0 && (
                        <div className="border border-border p-3 bg-muted/20" data-testid="cover-fees-section">
                            <label className="flex items-start gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={coverFees} onChange={e => setCoverFees(e.target.checked)} className="mt-1" data-testid="cover-fees-toggle" />
                                <span>
                                    <span className="font-medium">{t("giving.coverFees")}</span>{" "}
                                    <span className="text-muted-foreground">— {t("giving.coverFeesDesc", { amount: feesAdded.toFixed(2), gift: roundedAmt.toFixed(2), org: data.org.name })}</span>
                                </span>
                            </label>
                            {coverFees && (
                                <div className="mt-3 pt-3 border-t border-border text-sm space-y-1" data-testid="cover-fees-breakdown">
                                    <div className="flex justify-between"><span className="text-muted-foreground">{t("giving.yourGift")}</span><span className="data-num">${roundedAmt.toFixed(2)}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">{t("giving.feesCovering")}</span><span className="data-num">+${feesAdded.toFixed(2)}</span></div>
                                    <div className="flex justify-between font-semibold"><span>{frequency === "one-time" ? t("giving.totalCharge") : frequency === "monthly" ? t("giving.totalPerMonth") : frequency === "quarterly" ? t("giving.totalPerQuarter") : t("giving.totalPerYear")}</span><span className="data-num text-accent" data-testid="cover-fees-total">${total.toFixed(2)}</span></div>
                                </div>
                            )}
                        </div>
                    )}

                    {submitErr && <p className="text-destructive text-sm" data-testid="donate-error">{submitErr}</p>}
                    {verifyGate && (
                        <div className="bg-amber-50 border-2 border-amber-500 p-4 space-y-3" data-testid="donate-verify-gate">
                            <p className="font-bold text-amber-900 text-base flex items-center gap-2">
                                <span className="text-xl">✉</span> Confirm your email to complete this gift
                            </p>
                            <p className="text-sm text-amber-900">
                                We sent a one-click confirmation link to <strong>{verifyGate.email}</strong>. Click the link in your inbox, then come back here and tap Pay with Card again.
                            </p>
                            <p className="text-xs text-amber-800">
                                Why? It keeps your tax receipt out of someone else's spam folder.
                            </p>
                            <div className="flex gap-2">
                                <button type="button" onClick={resendVerifyAndRetry} disabled={resendingVerify}
                                        className="border border-amber-700 text-amber-900 px-4 py-2 text-xs uppercase tracking-wider font-bold disabled:opacity-50"
                                        data-testid="donate-verify-resend">
                                    {resendingVerify ? "Sending…" : "Resend link"}
                                </button>
                                <button type="button" onClick={() => setVerifyGate(null)}
                                        className="text-amber-800 px-3 py-2 text-xs uppercase tracking-wider underline"
                                        data-testid="donate-verify-dismiss">
                                    I clicked it — try again
                                </button>
                            </div>
                        </div>
                    )}
                    <button type="submit" disabled={busy} className="btn-accent w-full" data-testid="donate-submit">
                        {busy ? t("giving.processing") : frequency === "one-time" ? (
                            <>{t("giving.payCard", { amount: total.toFixed(2) })} <ArrowRight size={16} weight="bold" /></>
                        ) : (
                            <>{t("giving.startGiving", { frequency: frequency === "monthly" ? t("giving.monthly") : frequency === "quarterly" ? t("giving.quarterly") : t("giving.yearly"), amount: total.toFixed(2), period: frequency === "monthly" ? t("giving.perMo") : frequency === "quarterly" ? t("giving.perQtr") : t("giving.perYr") })} <ArrowRight size={16} weight="bold" /></>
                        )}
                    </button>

                    <CryptoDonateButton orgId={org.id || slug} orgName={org.name} />

                    {frequency === "one-time" && (
                        <>
                            <div className="flex items-center gap-2 my-2">
                                <div className="flex-1 h-px bg-border" />
                                <span className="text-xs label-eyebrow text-muted-foreground">{t("giving.or")}</span>
                                <div className="flex-1 h-px bg-border" />
                            </div>
                            <PayPalButtons
                                captureType="donation"
                                validate={() => {
                                    if (!name.trim()) return t("giving.errName");
                                    if (!email.trim()) return t("giving.errEmail");
                                    if (!(parseFloat(amount) > 0)) return t("giving.errAmount");
                                    return null;
                                }}
                                createOrder={async () => {
                                    const { data: res } = await api.post("/paypal/donations/create-order", {
                                        org_slug: slug,
                                        giving_page_slug: pageSlug,
                                        amount: roundedAmt,
                                        cover_fees: coverFees,
                                        lang: (i18n.language || "en").split("-")[0],
                                        donor_name: name,
                                        donor_email: email,
                                        message,
                                        is_anonymous: anon,
                                    });
                                    return res.order_id;
                                }}
                                onSuccess={() => {
                                    window.location.href = `/payment/success?session_id=paypal_success&provider=paypal`;
                                }}
                                onError={(e) => setSubmitErr(e?.response?.data?.detail || e?.message || t("giving.paypalError"))}
                            />
                        </>
                    )}
                </form>
            </div>

            <StickyGiveBar
                targetId="donate-form"
                ctaLabel={t("giving.giveNow")}
                contextLabel={t("giving.raisedSoFar")}
                contextValue={`$${page.total_raised.toLocaleString()}`}
            />

            <DonorInstallStrip orgName={org.name} />
            <Footer />
            <ConciergeWidget slug={slug} />
        </div>
    );
}
